import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Mail, User } from "lucide-react";

import { useLang, useLangPrefix } from "@/contexts/lang";

export const Route = createFileRoute("/about_/hotelski-menadzment")({
	headers: () => ({
		"Cache-Control":
			"public, max-age=0, s-maxage=86400, stale-while-revalidate=604800",
	}),
	head: () => ({
		meta: [
			{ title: "Хотелски менаџмент | Македонијатурист АД" },
			{
				name: "description",
				content:
					"Запознајте го тимот на хотелски менаџери на Македонијатурист АД. Професионалност и искуство во водење на нашите хотелски капацитети.",
			},
		],
		links: [
			{
				rel: "canonical",
				href: "https://makedonijaturist.com.mk/about/hotelski-menadzment",
			},
		],
	}),
	component: HotelskiMenadzmentPage,
});

const managerNames = [
	"Ime Prezime",
	"Ime Prezime",
	"Ime Prezime",
	"Ime Prezime",
];

const managerEmails = [
	"gm.holidayinn@makedonijaturist.com.mk",
	"gm.karposh@makedonijaturist.com.mk",
	"gm.turist@makedonijaturist.com.mk",
	"gm.bellevue@makedonijaturist.com.mk",
];

export function HotelskiMenadzmentPage() {
	const t = useLang();
	const prefix = useLangPrefix();

	return (
		<main className="bg-background">
			{/* Hero */}
			<section className="mt-grain relative overflow-hidden bg-primary text-white">
				<div className="pointer-events-none absolute inset-0">
					<div className="absolute -top-[20%] right-0 h-[600px] w-[600px] rounded-full bg-[radial-gradient(closest-side,rgba(201,161,90,0.12),transparent)] blur-[100px]" />
					<div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
				</div>
				<div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16 md:py-28">
					<a
						href={`${prefix}/about`}
						className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-white/60 hover:text-white/90 transition-colors duration-300 mb-6 sm:mb-8"
					>
						<ArrowLeft className="h-3.5 w-3.5" />
						{t.hotelskiMenadzment.backLink}
					</a>
					<div className="max-w-3xl">
						<p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">
							{t.hotelskiMenadzment.orgLabel}
						</p>
						<h1 className="mt-4 sm:mt-6 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08]">
							{t.hotelskiMenadzment.heading}
						</h1>
						<p className="mt-4 sm:mt-6 max-w-2xl text-base sm:text-lg text-white/80 font-light leading-relaxed">
							{t.hotelskiMenadzment.intro}
						</p>
					</div>
				</div>
			</section>

			{/* Managers */}
			<section className="bg-background py-12 sm:py-16 md:py-24">
				<div className="mx-auto max-w-7xl px-4 sm:px-6">
					<div className="mb-10 sm:mb-14">
						<span className="inline-block h-1 w-12 bg-accent mb-4 sm:mb-6" />
						<h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight text-primary leading-[1.1]">
							{t.hotelskiMenadzment.managersHeading}
						</h2>
					</div>

					<div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
						{t.hotelskiMenadzment.managers.map((manager, idx) => (
							<div
								key={manager.hotel}
								className="rounded-xl md:rounded-2xl border border-border bg-card shadow-sm overflow-hidden"
							>
								{/* Photo placeholder */}
								<div className="relative h-56 sm:h-64 overflow-hidden bg-gradient-to-br from-secondary to-muted flex items-end justify-center">
									<User
										className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[60%] h-24 w-24 text-primary/10"
										strokeWidth={1}
									/>
									<div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
								</div>

								{/* Content */}
								<div className="p-6">
									<p className="text-xs font-bold tracking-[0.18em] uppercase text-accent mb-1">
										{t.hotelskiMenadzment.position}
									</p>
									<h3 className="font-display text-lg sm:text-xl font-medium text-primary leading-snug mb-1">
										{managerNames[idx] ?? "Ime Prezime"}
									</h3>
									<p className="text-xs font-medium text-muted-foreground mb-3">
										{manager.hotel}
									</p>
									<p className="text-sm text-foreground/70 font-light leading-relaxed mb-4">
										{manager.description}
									</p>
									<a
										href={`mailto:${managerEmails[idx]}`}
										className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors break-all"
									>
										<Mail className="h-3 w-3 shrink-0 text-accent" />
										{managerEmails[idx]}
									</a>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>
		</main>
	);
}
